import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords, update } from '../services/ContactsService';
import { useState } from 'react';
import { Contact, ContactStatus } from '../types';
import { useContactsSelection } from './useContactsSelection';

export const useContacts = (externalStatusFilter?: ContactStatus | null) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const { selection } = useContactsSelection();

    // Use external filter if provided, otherwise no filter
    const activeStatusFilter = externalStatusFilter !== undefined ? externalStatusFilter : null;

    // INFINITE QUERY
    const queryKey = ['contacts', keyword, activeStatusFilter, limit];

    const contactsQuery = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({
            limit,
            nextToken: pageParam,
            statusFilter: activeStatusFilter,
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: 0
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: async ({ id, record }: { id: string; record: Contact }) => {
            return update(id, record);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
        }
    });

    const fetchRecordsAction = () => {
        if (contactsQuery.hasNextPage) {
            contactsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const updateRecord = (id: string, record: Contact) => {
        updateMutation.mutate({ id, record });
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleItemsPerPageChange = (_pageSize: string | null) => {
        // Not used in infinite query pattern
    };

    const allItems = contactsQuery.data?.pages?.flatMap(page => 
        (page && 'items' in page) ? page.items : []
    ) || [];

    const refetchRecords = () => {
        queryClient.invalidateQueries({ queryKey: ['contacts'] });
    };

    return {
        items: allItems,
        visibleItems: allItems,
        loading: contactsQuery.isLoading,
        isFetchingNextPage: contactsQuery.isFetchingNextPage,
        keyword,
        limit: limit.toString(),
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: contactsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        refetchRecords,
        deleteRecords,
        updateRecord,
        handleFilterByKeyword,
        handleItemsPerPageChange,
        deleteMutation,
        updateMutation
    };
};
